import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Check/X icons for comparison table
function Check() {
  return (
    <svg className="w-5 h-5 text-hvme-primary mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function X() {
  return (
    <svg className="w-5 h-5 text-hvme-text-muted mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// =============================================================================
// Types & Data
// =============================================================================

interface PricingTier {
  name: string;
  price: string;
  priceAnnual: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

const PRICING_TIERS: PricingTier[] = [
  {
    name: "Starter",
    price: "Free",
    priceAnnual: "Free",
    description: "Perfect for exploring and small projects",
    features: [
      "100 AI Credits/month",
      "50 FHE Units/month",
      "Single agent deployment",
      "Community support",
      "7-day data retention",
    ],
    cta: "Start Free",
  },
  {
    name: "Pro",
    price: "$49",
    priceAnnual: "$39",
    description: "For teams building production applications",
    features: [
      "5,000 AI Credits/month",
      "2,500 FHE Units/month",
      "Multi-agent mesh (up to 5)",
      "Priority email support",
      "Full API access",
      "30-day data retention",
    ],
    cta: "Start Pro Trial",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    priceAnnual: "Custom",
    description: "For organizations with advanced needs",
    features: [
      "Unlimited AI Credits",
      "Unlimited FHE Units",
      "Unlimited agents",
      "Dedicated support engineer",
      "On-premise deployment",
      "Custom SLA & SOC 2",
    ],
    cta: "Contact Sales",
  },
];

const FAQ_ITEMS = [
  {
    question: "What's the difference between AI Credits and FHE Units?",
    answer:
      "AI Credits power HVME's Human-Centric AI inference operations - agent orchestration, reasoning, and decision-making. FHE Units are for Fully Homomorphic Encryption operations - encrypted computations on sensitive data. They're separate because AI and cryptographic workloads have different resource requirements.",
  },
  {
    question: "Can I upgrade or downgrade anytime?",
    answer:
      "Yes. Upgrade instantly, downgrade at the end of your billing period. Unused credits don't roll over, but you can purchase additional AI Credits or FHE Units anytime.",
  },
  {
    question: "Is there a free trial for Pro?",
    answer:
      "Yes, Pro includes a 14-day free trial with full features. No credit card required to start.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit cards, wire transfers for Enterprise, and crypto payments (BTC, ETH, USDC).",
  },
  {
    question: "Do you offer refunds?",
    answer:
      "We offer a 30-day money-back guarantee for annual plans. Monthly plans can be canceled anytime.",
  },
];

// =============================================================================
// Pricing Card Component
// =============================================================================

function PricingCard({
  tier,
  isAnnual,
  index,
  onSelect,
}: {
  tier: PricingTier;
  isAnnual: boolean;
  index: number;
  onSelect?: (tierName: string) => void;
}) {
  const price = isAnnual ? tier.priceAnnual : tier.price;
  const isCustom = price === "Custom" || price === "Free";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`
        relative flex flex-col p-8 rounded-3xl border
        ${tier.highlighted
          ? "border-hvme-primary/50 bg-hvme-bg-mid shadow-primary-glow"
          : "border-hvme-border bg-hvme-bg-surface"
        }
      `}
    >
      {/* Popular badge */}
      {tier.highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-hvme-primary to-hvme-accent text-white">
            Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h3 className="font-heading font-bold text-xl text-hvme-text-primary mb-2">
          {tier.name}
        </h3>
        <p className="font-body text-sm text-hvme-text-muted">
          {tier.description}
        </p>
      </div>

      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="font-heading font-black text-4xl text-hvme-text-primary">
            {price}
          </span>
          {!isCustom && (
            <span className="font-body text-sm text-hvme-text-muted">/month</span>
          )}
        </div>
        {isAnnual && !isCustom && price !== "Free" && (
          <p className="text-xs text-hvme-accent mt-1">Save 20% with annual</p>
        )}
      </div>

      {/* Features */}
      <ul className="flex-1 space-y-3 mb-8">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-hvme-primary flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-body text-sm text-hvme-text-secondary">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <motion.button
        onClick={() => onSelect?.(tier.name)}
        whileHover={{
          scale: 1.02,
          boxShadow: tier.highlighted
            ? "0 0 30px rgba(220,160,250,0.4), 0 0 60px rgba(220,160,250,0.2)"
            : "0 0 20px rgba(220,160,250,0.2)",
        }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full py-3 rounded-full font-body font-semibold transition-all duration-300 cursor-pointer
          ${tier.highlighted
            ? "bg-hvme-primary text-white"
            : "bg-hvme-bg-deep text-hvme-text-primary border border-hvme-border hover:border-hvme-primary/50"
          }
        `}
      >
        {tier.cta}
      </motion.button>
    </motion.div>
  );
}

// =============================================================================
// FAQ Accordion Component
// =============================================================================

function FAQItem({
  question,
  answer,
  index,
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="border-b border-hvme-border"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left"
      >
        <span className="font-body font-medium text-hvme-text-primary">
          {question}
        </span>
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-5 h-5 text-hvme-text-muted flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-5 font-body text-sm text-hvme-text-secondary">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// =============================================================================
// Pricing Page Component
// =============================================================================

interface PricingPageProps {
  onGetStarted?: () => void;
}

export function PricingPage({ onGetStarted }: PricingPageProps) {
  const [isAnnual, setIsAnnual] = useState(true);

  const handleSelectTier = (tierName: string) => {
    // For Enterprise, could open a contact form
    // For now, navigate to console for all tiers
    onGetStarted?.();
  };

  return (
    <div className="min-h-screen py-20">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <h1
            className="font-heading font-black text-4xl md:text-6xl tracking-tight mb-6"
            style={{
              background: "linear-gradient(135deg, #DCA0FA, #E6BEFA)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Simple, Transparent Pricing
          </h1>
          <p className="font-body text-lg text-hvme-text-secondary mb-8">
            Start free, scale as you grow. No hidden fees, no surprises.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-1.5 rounded-full bg-hvme-bg-surface border border-hvme-border">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                !isAnnual
                  ? "bg-hvme-primary text-white"
                  : "text-hvme-text-muted hover:text-hvme-text-secondary"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isAnnual
                  ? "bg-hvme-primary text-white"
                  : "text-hvme-text-muted hover:text-hvme-text-secondary"
              }`}
            >
              Annual
              <span className="ml-2 text-xs text-hvme-accent">-20%</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Pricing Cards */}
      <div className="mx-auto max-w-7xl px-6 mb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PRICING_TIERS.map((tier, index) => (
            <PricingCard
              key={tier.name}
              tier={tier}
              isAnnual={isAnnual}
              index={index}
              onSelect={handleSelectTier}
            />
          ))}
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="mx-auto max-w-5xl px-6 mb-24">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-heading font-bold text-2xl text-hvme-text-primary text-center mb-10"
        >
          Compare Plans
        </motion.h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-hvme-border">
                <th className="text-left py-4 px-4 font-body text-sm text-hvme-text-muted">Feature</th>
                <th className="text-center py-4 px-4 font-body text-sm text-hvme-text-secondary">Starter</th>
                <th className="text-center py-4 px-4 font-body text-sm text-hvme-primary">Pro</th>
                <th className="text-center py-4 px-4 font-body text-sm text-hvme-text-secondary">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "AI Credits", starter: "100/mo", pro: "5,000/mo", enterprise: "Unlimited" },
                { feature: "FHE Units", starter: "50/mo", pro: "2,500/mo", enterprise: "Unlimited" },
                { feature: "Agents", starter: "1", pro: "Up to 5", enterprise: "Unlimited" },
                { feature: "Data Retention", starter: "7 days", pro: "30 days", enterprise: "Custom" },
                { feature: "API Access", starter: false, pro: true, enterprise: true },
                { feature: "Priority Support", starter: false, pro: true, enterprise: true },
                { feature: "On-Premise Deployment", starter: false, pro: false, enterprise: true },
                { feature: "SLA Guarantee", starter: false, pro: false, enterprise: true },
                { feature: "SOC 2 Compliance", starter: false, pro: false, enterprise: true },
              ].map((row, i) => (
                <tr key={row.feature} className="border-b border-hvme-border/50">
                  <td className="py-4 px-4 font-body text-sm text-hvme-text-primary">{row.feature}</td>
                  <td className="text-center py-4 px-4">
                    {typeof row.starter === "boolean" ? (
                      row.starter ? <Check /> : <X />
                    ) : (
                      <span className="font-body text-sm text-hvme-text-secondary">{row.starter}</span>
                    )}
                  </td>
                  <td className="text-center py-4 px-4 bg-hvme-primary/5">
                    {typeof row.pro === "boolean" ? (
                      row.pro ? <Check /> : <X />
                    ) : (
                      <span className="font-body text-sm text-hvme-text-primary">{row.pro}</span>
                    )}
                  </td>
                  <td className="text-center py-4 px-4">
                    {typeof row.enterprise === "boolean" ? (
                      row.enterprise ? <Check /> : <X />
                    ) : (
                      <span className="font-body text-sm text-hvme-text-secondary">{row.enterprise}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mx-auto max-w-3xl px-6">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-heading font-bold text-2xl text-hvme-text-primary text-center mb-10"
        >
          Frequently Asked Questions
        </motion.h2>
        <div className="space-y-0">
          {FAQ_ITEMS.map((item, index) => (
            <FAQItem key={item.question} {...item} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
}
